import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Check, X, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HScroll from '../components/HScroll';
import { useContent } from '../content';
import { useLang } from '../i18n';
import { useViewContext } from '../viewContext';

// Themed, non-copyrighted (Unsplash/Pexels) imagery per service.
const SERVICE_IMAGES = {
  'intermodal-transport-engineering': 'https://images.unsplash.com/photo-1741376659226-aa43dd378019?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  'heavy-lift-engineering': 'https://images.unsplash.com/photo-1784915477040-00969e627588?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  'marine-survey-port-captain-services': 'https://images.unsplash.com/photo-1769776208566-ac17d0b9e955?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  'equipment-labor-sourcing': 'https://images.pexels.com/photos/34926604/pexels-photo-34926604.jpeg?auto=compress&cs=tinysrgb&w=1400',
  'integrated-port-services': 'https://images.unsplash.com/photo-1767250752542-26f218107359?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  'marine-logistics': 'https://images.unsplash.com/photo-1741554678792-c48cc08fe42a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400'
};
const SECONDARY_IMAGES = {
  'intermodal-transport-engineering': 'https://images.pexels.com/photos/29278107/pexels-photo-29278107.jpeg?auto=compress&cs=tinysrgb&w=1400',
  'heavy-lift-engineering': 'https://images.pexels.com/photos/34926604/pexels-photo-34926604.jpeg?auto=compress&cs=tinysrgb&w=1400',
  'marine-survey-port-captain-services': 'https://images.unsplash.com/photo-1772009444404-25680043a19a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  'equipment-labor-sourcing': 'https://images.unsplash.com/photo-1784915477040-00969e627588?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  'integrated-port-services': 'https://images.unsplash.com/photo-1741376659226-aa43dd378019?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  'marine-logistics': 'https://images.unsplash.com/photo-1769776208566-ac17d0b9e955?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400'
};

// Per-service image galleries (project-library style). Add real photos here per slug.
const SERVICE_GALLERIES = {
  'intermodal-transport-engineering': [
    '/brand/intermodal/render1.webp',
    '/brand/intermodal/render2.webp',
    '/brand/intermodal/risk.png',
    '/brand/intermodal/calc-transformer-combined.png',
    '/brand/intermodal/calc-lashcon.png',
    '/brand/intermodal/stow1.png',
    '/brand/intermodal/stow2.png',
    '/brand/intermodal/stow3.png',
    '/brand/intermodal/stow4.png',
    '/brand/intermodal/stow5-rot.png',
    '/brand/intermodal/stow6.png',
    '/brand/intermodal/stow7.png',
    '/brand/intermodal/stow8.png',
    '/brand/intermodal/stow9.webp',
    '/brand/intermodal/stow10.webp',
    '/brand/intermodal/stow11.webp',
    '/brand/intermodal/stow12.png'
  ],
  'heavy-lift-engineering': [
    '/brand/heavylift/render1.png',
    '/brand/heavylift/rigging.webp',
    '/brand/heavylift/calc.png',
    '/brand/heavylift/hl4.png',
    '/brand/heavylift/hl5.webp',
    '/brand/heavylift/hl6.webp',
    '/brand/heavylift/hl7.webp',
    '/brand/heavylift/hl8.png',
    '/brand/heavylift/hl9.png'
  ],
  'integrated-port-services': [
    '/brand/port/p1.webp',
    '/brand/port/p3.webp',
    '/brand/port/p4.webp',
    '/brand/port/p5.webp'
  ],
  'equipment-labor-sourcing': [
    '/brand/equipment/e1.webp',
    '/brand/equipment/e2.webp',
    '/brand/equipment/e3.webp',
    '/brand/equipment/e4.webp',
    '/brand/equipment/e5.webp'
  ],
  'marine-survey-port-captain-services': [
    '/brand/survey/m1.webp',
    '/brand/survey/m2.webp',
    '/brand/survey/m3.jpg',
    '/brand/survey/m4.webp',
    '/brand/survey/m5.webp',
    '/brand/survey/m6.webp',
    '/brand/survey/m7.webp',
    '/brand/survey/m8.webp',
    '/brand/survey/m9.png',
    '/brand/survey/m10.webp',
    '/brand/survey/m11.png',
    '/brand/survey/m12.jpg',
    '/brand/survey/m13.webp',
    '/brand/survey/m14.webp'
  ]
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { serviceDetails, serviceScopeIntro } = useContent();
  const { t } = useLang();
  const data = serviceDetails[slug];
  const heroImg = SERVICE_IMAGES[slug] || SERVICE_IMAGES['intermodal-transport-engineering'];
  const secondaryImg = SECONDARY_IMAGES[slug] || SECONDARY_IMAGES['intermodal-transport-engineering'];
  const gallery = SERVICE_GALLERIES[slug] || [];
  const [lightbox, setLightbox] = useState(null);
  const { setView } = useViewContext();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Report the current view to Dom so it can answer about this page / open photo.
  useEffect(() => {
    if (!data) return;
    if (lightbox !== null && gallery[lightbox]) {
      setView(`The visitor is viewing an enlarged photo (image ${lightbox + 1} of ${gallery.length}) in the gallery of the "${data.title}" service page.`);
    } else {
      setView(`The visitor is on the "${data.title}" service page (service detail).`);
    }
    return () => setView('');
  }, [data, lightbox, gallery.length, setView]);

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox !== null) {
        if (e.key === 'Escape') setLightbox(null);
        else if (e.key === 'ArrowRight') setLightbox((v) => (v + 1) % gallery.length);
        else if (e.key === 'ArrowLeft') setLightbox((v) => (v - 1 + gallery.length) % gallery.length);
        return;
      }
      if (e.key === 'Escape') navigate('/');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, lightbox, gallery.length]);

  const goHomeTab = (tab) => navigate('/', { state: { tab } });

  if (!data) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="bg-navy min-h-screen">
      <Header activeTab="capabilities" onTabChange={goHomeTab} onLogoClick={() => navigate('/')} />

      <main className="section-teal grid-overlay">
        <div className="max-w-[1680px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-24 relative">
          {/* Back */}
          <button
            onClick={() => goHomeTab('capabilities')}
            data-testid="back-to-services"
            className="group inline-flex items-center gap-2.5 rounded-full bg-ember hover:bg-ember-hover text-white px-5 py-3 label-mono shadow-lg shadow-ember/20 transition-colors"
          >
            <ArrowLeft size={17} strokeWidth={2.2} className="transition-transform group-hover:-translate-x-1" />
            {t('ui.backToServices')}
          </button>

          {/* Title block */}
          <div className="mt-12 max-w-4xl">
            <div className="label-mono text-ember mb-6">{data.index} · {t('ui.service')}</div>
            <h1 className="font-display font-light text-white text-4xl md:text-6xl lg:text-[72px] leading-[1.02] tracking-tight">
              {data.title}
            </h1>
            <p className="mt-7 text-white/75 font-body text-lg md:text-xl leading-relaxed max-w-2xl">{data.tagline}</p>
          </div>

          {/* Overview (full width) */}
          <div className="mt-12">
            <div
              className="rounded-xl border border-white/12 p-8 md:p-10"
              style={{ background: 'linear-gradient(160deg, rgba(60,124,134,0.22) 0%, rgba(12,46,56,0.35) 100%)' }}
            >
              <div className="label-mono text-ember mb-5">{t('ui.overview')}</div>
              <div className="grid md:grid-cols-2 gap-x-10 gap-y-4">
                {data.overview.map((p, i) => (
                  <p key={i} className="text-white/80 font-body text-base md:text-[17px] leading-relaxed">{p}</p>
                ))}
              </div>
              {data.blocks && (
                <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-6 border-t border-white/10 pt-8">
                  {data.blocks.map((b) => (
                    <div key={b.heading}>
                      <h3 className="font-display font-medium text-white text-lg md:text-xl mb-2">{b.heading}</h3>
                      <p className="text-white/70 font-body text-base leading-relaxed">{b.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Image gallery — project-library style */}
            {gallery.length > 0 && (
              <div className="mt-10">
                <div className="label-mono text-white/45 mb-4">{t('proj.gallery')}</div>
                <HScroll className="pb-2" data-testid="service-gallery" arrows>
                  {gallery.map((src, i) => (
                    <button
                      key={i}
                      data-testid={`service-gallery-item-${i}`}
                      onClick={() => setLightbox(i)}
                      className="group relative shrink-0 w-72 sm:w-80 aspect-[16/10] rounded-lg overflow-hidden border border-white/12 hover:border-ember/50 transition-colors"
                    >
                      <img src={src} alt={`${data.title} ${i + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" draggable="false" />
                      <div className="absolute inset-0 bg-navy/10 group-hover:bg-navy/0 transition-colors" />
                    </button>
                  ))}
                </HScroll>
              </div>
            )}
          </div>
        </div>

        {/* Scope section */}
        <div className="max-w-[1680px] mx-auto px-6 md:px-10 pb-28 relative">
          <div className="grid lg:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] gap-12 lg:gap-16 items-start">
            {/* Left */}
            <div className="lg:pt-6 lg:sticky lg:top-28">
              <div className="label-mono text-white/45 mb-6">{t('ui.scope')}</div>
              <h2 className="font-display font-medium text-white text-3xl md:text-4xl leading-tight">{t('ui.servicesInclude')}</h2>
              <p className="mt-6 text-white/65 font-body text-base leading-relaxed max-w-md">{serviceScopeIntro}</p>
              <button
                onClick={() => goHomeTab('contact')}
                data-testid="discuss-project"
                className="group mt-8 inline-flex items-center gap-3 bg-ember hover:bg-ember-hover text-white px-7 py-4 label-mono transition-colors"
              >
                {t('ui.discussProject')}
                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>

            {/* Right: scope grid */}
            <div>
              <div className="grid sm:grid-cols-2 gap-5">
                {data.scope.map((item, i) => (
                  <div
                    key={item}
                    data-testid={`scope-item-${i + 1}`}
                    className="group flex items-start gap-4 rounded-lg border border-white/12 px-5 py-5 transition-all duration-300 hover:border-ember/50 hover:bg-white/[0.03]"
                    style={{ background: 'linear-gradient(150deg, rgba(60,124,134,0.14) 0%, rgba(12,46,56,0.28) 100%)' }}
                  >
                    <span className="mt-1 flex items-center justify-center w-5 h-5 rounded-full border border-ember/50 shrink-0">
                      <Check size={11} strokeWidth={3} className="text-ember" />
                    </span>
                    <div>
                      <div className="label-mono text-white/40">{String(i + 1).padStart(2, '0')}</div>
                      <div className="mt-1 font-display font-medium text-white text-lg leading-snug">{item}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      {lightbox !== null && gallery[lightbox] && (
        <div
          data-testid="service-lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[90] bg-navy/95 backdrop-blur-sm flex items-center justify-center p-6"
        >
          <button
            onClick={() => setLightbox(null)}
            aria-label="Close"
            className="absolute top-6 right-6 text-white/70 hover:text-ember transition-colors z-10"
          >
            <X size={28} />
          </button>
          {gallery.length > 1 && (
            <>
              <button
                data-testid="lightbox-prev"
                onClick={(e) => { e.stopPropagation(); setLightbox((v) => (v - 1 + gallery.length) % gallery.length); }}
                aria-label="Previous image"
                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-ember text-white flex items-center justify-center transition-colors"
              >
                <ChevronLeft size={26} />
              </button>
              <button
                data-testid="lightbox-next"
                onClick={(e) => { e.stopPropagation(); setLightbox((v) => (v + 1) % gallery.length); }}
                aria-label="Next image"
                className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-ember text-white flex items-center justify-center transition-colors"
              >
                <ChevronRight size={26} />
              </button>
            </>
          )}
          <img
            key={lightbox}
            src={gallery[lightbox]}
            alt=""
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            draggable="false"
          />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 label-mono text-white/60">
            {lightbox + 1} / {gallery.length}
          </div>
        </div>
      )}

      <Footer onTabChange={goHomeTab} />
    </div>
  );
};

export default ServiceDetail;
